import { apiClient, buildApiUrl } from '../lib/apiClient';

export interface PresignedUploadUrlResult {
  uploadUrl: string;
  objectKey: string;
}

export interface ConfirmPhotoUploadResult {
  imageUrl: string;
  aquariumId: string;
}

/**
 * Upload an aquarium photo using the existing multipart endpoint with XHR for progress tracking.
 * Returns a ConfirmPhotoUploadResult with the stored imageUrl and aquariumId.
 */
export async function uploadAquariumPhoto(
  aquariumId: string,
  file: File,
  onProgress?: (percent: number) => void,
): Promise<ConfirmPhotoUploadResult> {
  return new Promise<ConfirmPhotoUploadResult>((resolve, reject) => {
    const formData = new FormData();
    formData.append('image', file);

    const xhr = new XMLHttpRequest();

    if (onProgress) {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });
    }

    xhr.addEventListener('load', () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const body = JSON.parse(xhr.responseText) as { data?: { imageUrl?: string; aquariumId?: string } };
          const imageUrl = body.data?.imageUrl ?? '';
          const returnedId = body.data?.aquariumId ?? aquariumId;
          resolve({ imageUrl, aquariumId: returnedId });
        } catch {
          reject(new Error('Failed to parse upload response'));
        }
      } else {
        let message = `Upload failed with status ${xhr.status}`;
        try {
          const errBody = JSON.parse(xhr.responseText) as { error?: { message?: string } };
          if (errBody.error?.message) {
            message = errBody.error.message;
          }
        } catch {
          // ignore parse error, use default message
        }
        reject(new Error(message));
      }
    });

    xhr.addEventListener('error', () => {
      reject(new Error('Network error during upload'));
    });

    xhr.addEventListener('abort', () => {
      reject(new Error('Upload aborted'));
    });

    xhr.open('POST', buildApiUrl(`/api/v1/aquariums/${aquariumId}/image`));

    // Include auth token if available
    const token = localStorage.getItem('accessToken');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    xhr.send(formData);
  });
}

/**
 * Delete the photo associated with an aquarium.
 */
export async function deleteAquariumPhoto(aquariumId: string): Promise<void> {
  await apiClient.delete(`/api/v1/aquariums/${aquariumId}/image`);
}

/**
 * Confirm a presigned upload by notifying the backend of the uploaded object key.
 * This is used in the presigned URL flow (sub-issue 1 backend endpoint).
 * Provided here for future use — the hook uses uploadAquariumPhoto (multipart) for now.
 */
export async function confirmPhotoUpload(
  aquariumId: string,
  objectKey: string,
): Promise<ConfirmPhotoUploadResult> {
  const response = await apiClient.post<{ data: ConfirmPhotoUploadResult }>(
    `/api/v1/aquariums/${aquariumId}/photo/confirm`,
    { objectKey },
  );
  return response.data;
}

/**
 * Fetch a presigned PUT URL for direct S3 upload.
 */
export async function getPresignedUploadUrl(
  aquariumId: string,
  contentType: string,
): Promise<PresignedUploadUrlResult> {
  const response = await apiClient.get<{ data: PresignedUploadUrlResult }>(
    `/api/v1/aquariums/${aquariumId}/photo/upload-url?contentType=${encodeURIComponent(contentType)}`,
  );
  return response.data;
}
